import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FaPaperclip, FaTimes, FaEye, FaPlus, FaTrash, FaShieldAlt, FaListOl } from 'react-icons/fa';
import { riskAssessmentAPI } from '../services/api';
import FloatingScrollWrapper from './FloatingScrollWrapper';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

// Mirrors backend/services/riskAssessmentEngine.js — proposed controls become
// mandatory from the Medium band (RPN >= 5); the server re-checks on submit.
export const PROPOSED_CONTROL_MIN_RPN = 5;
export const FMEA_ACTION_STATUSES = ['Open', 'In Progress', 'Completed', 'Verified'];

const product = (a, b, c) => (a && b && c ? Number(a) * Number(b) * Number(c) : null);
export const rowRpn = (it) => product(it.severity, it.occurrence, it.detectability);
export const rowResidualRpn = (it) => product(it.revised_severity, it.revised_occurrence, it.revised_detectability);

const rpnBand = (rpn) => (rpn >= 18 ? 'danger' : rpn >= 10 ? 'warning' : rpn >= 5 ? 'warning' : 'success');
const hasEvidence = (ref, files) => !!String(ref || '').trim() || (files || []).length > 0;
const blank = (v) => !String(v || '').trim();

// One row per risk factor of the ranking, pre-filled with the assessment's own
// scores so the worksheet starts from what the assessment already says.
export const buildDefaultFmeaItems = (a) => {
  const severity = (a.is_genotoxic || a.is_carcinogenic || a.is_sensitizer) ? 3 : (Number(a.toxicity_risk_score) || 1);
  const occurrence = Number(a.occurrence_score) || 2;
  const detectability = Number(a.detectability_score) || 2;
  const row = (risk_factor, failure_mode, effect, cause, s, o) => ({
    id: `fm-${risk_factor.replace(/\W+/g, '-').toLowerCase()}-${Math.random().toString(36).slice(2, 7)}`,
    risk_factor, failure_mode, effect, cause,
    current_controls: '', severity: s, occurrence: o, detectability,
    proposed_controls: '', responsible: '', target_date: '', action_status: 'Open',
    evidence_ref: '', evidence_files: [],
    revised_severity: null, revised_occurrence: null, revised_detectability: null,
  });
  return [
    row('Toxicological', 'API residue carried over above the HBEL/ADE-based limit', 'Patient exposed to a pharmacologically active / toxic cross-contaminant', 'Inadequate removal of high-potency residue', severity, occurrence),
    row('Solubility', 'Poorly soluble residue not removed by cleaning agent / rinse', 'Residue remains on product-contact surfaces', 'Low solubility in the cleaning solvent', severity, Number(a.solubility_risk_score) || 1),
    row('Cleanability', 'Adherent residue retained at hard-to-clean locations', 'Carryover into the next batch', 'Product forms films / adheres to surfaces', severity, Number(a.cleanability_risk_score) || 1),
    row('Patient population', 'Cross-contaminant reaches a vulnerable patient population', 'Higher severity of adverse effect', 'Shared equipment also serves paediatric / oncology / immunocompromised products', Math.max(severity, Number(a.patient_population_risk) || 1), occurrence),
    row('Batch size', 'Small subsequent batch concentrates carried-over residue', 'Higher residue per dose of the next product', 'Small batch size of the next product', severity, Number(a.batch_size_risk) || 1),
    row('Equipment sharing', 'Cross-contamination through shared multi-product equipment', 'Contamination of the subsequent product', 'Equipment shared between products', severity, Number(a.equipment_risk_score) || 1),
  ];
};

// Client-side copy of validateRiskControls — shown as a checklist before submit.
export const riskControlProblems = (a) => {
  const problems = [];
  if (blank(a.current_controls)) problems.push('Current controls are required for the assessment.');
  if (a.risk_level && a.risk_level !== 'Low' && blank(a.proposed_controls)) problems.push(`Proposed controls are required — overall risk is ${a.risk_level}.`);
  if (!blank(a.proposed_controls) && !hasEvidence(a.control_evidence_ref, a.control_evidence_files)) problems.push('Evidence of the proposed action is required for the assessment.');
  const items = Array.isArray(a.fmea_items) ? a.fmea_items : [];
  if (!items.length) problems.push('The FMEA table needs at least one failure mode.');
  items.forEach((it, i) => {
    const label = `FMEA row ${i + 1}${it.failure_mode ? ` (${it.failure_mode})` : ''}`;
    const rpn = rowRpn(it);
    if (blank(it.failure_mode)) problems.push(`${label}: failure mode is required.`);
    if (!rpn) problems.push(`${label}: Severity, Occurrence and Detectability are required.`);
    if (blank(it.current_controls)) problems.push(`${label}: current controls are required.`);
    if (rpn >= PROPOSED_CONTROL_MIN_RPN && blank(it.proposed_controls)) problems.push(`${label}: proposed control is required (RPN ${rpn}).`);
    if (!blank(it.proposed_controls) && !hasEvidence(it.evidence_ref, it.evidence_files)) problems.push(`${label}: evidence of the proposed action is required.`);
  });
  return problems;
};

const missingStyle = (missing) => (missing ? { borderColor: 'var(--danger-border)', background: 'var(--danger-bg)' } : null);

const RpnPill = ({ value }) => {
  if (!value) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  const b = rpnBand(value);
  return (
    <span style={{ display: 'inline-block', minWidth: '30px', textAlign: 'center', padding: '2px 8px', borderRadius: '999px', fontWeight: 800, background: `var(--${b}-bg)`, color: `var(--${b}-text)`, border: `1px solid var(--${b}-border)` }}>
      {value}
    </span>
  );
};

// Reference no. + attachments, the same shape as the AMV reference cell in Acceptance Criteria.
export const EvidenceCell = ({ reference, files = [], onChange, readOnly, missing, compact }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await riskAssessmentAPI.uploadEvidence(fd);
      onChange({ reference, files: [...files, res.data.data] });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const fileLinks = files.map((f) => (
    <div key={f.filename} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--fs-xs)', marginTop: '2px' }}>
      <a href={`${API_ORIGIN}/uploads/${f.filename}`} target="_blank" rel="noopener noreferrer" title={f.originalname}
        style={{ color: 'var(--primary-color)', textDecoration: 'none', maxWidth: compact ? '140px' : '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
        <FaEye style={{ marginRight: '3px' }} />{f.originalname || f.filename}
      </a>
      {!readOnly && (
        <button type="button" title="Remove attachment" onClick={() => onChange({ reference, files: files.filter((x) => x.filename !== f.filename) })}
          style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--danger-color)', padding: '0 2px' }}>
          <FaTimes />
        </button>
      )}
    </div>
  ));

  if (readOnly) {
    return (
      <div style={{ minWidth: compact ? '150px' : undefined }}>
        {reference ? <div style={{ fontSize: 'var(--fs-sm)' }}>{reference}</div> : (!files.length && <span style={{ color: 'var(--text-secondary)' }}>—</span>)}
        {fileLinks}
      </div>
    );
  }

  return (
    <div style={{ minWidth: compact ? '170px' : undefined }}>
      <input className="form-control form-control-sm" value={reference || ''} placeholder="Evidence ref. (CC / CAPA / doc no.)"
        onChange={(e) => onChange({ reference: e.target.value, files })} style={{ fontSize: 'var(--fs-sm)', ...missingStyle(missing) }} />
      {fileLinks}
      <input ref={inputRef} type="file" style={{ display: 'none' }} onChange={handleFile} />
      <button type="button" className="btn btn-sm btn-outline" disabled={uploading} onClick={() => inputRef.current?.click()}
        style={{ marginTop: '4px', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: 'var(--fs-xs)', padding: '2px 8px' }}>
        <FaPaperclip /> {uploading ? 'Uploading…' : 'Attach'}
      </button>
    </div>
  );
};

const cardStyle = { background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '20px', marginBottom: '20px' };
const headingStyle = { margin: '0 0 14px 0', fontSize: 'var(--fs-lg)', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' };

// Assessment-level controls: what is in place, what is proposed, proof it was done.
export const RiskControlSection = ({ value, onChange, readOnly, riskLevel }) => {
  const needsProposed = riskLevel && riskLevel !== 'Low';
  const evidenceMissing = !blank(value.proposed_controls) && !hasEvidence(value.control_evidence_ref, value.control_evidence_files);
  const field = (key, label, required, placeholder) => (
    <div>
      <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>{label}{required && ' *'}</label>
      {readOnly
        ? <p style={{ margin: 0, fontSize: 'var(--fs-base)', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{value[key] || '—'}</p>
        : <textarea className="form-control form-control-sm" rows={3} value={value[key] || ''} placeholder={placeholder}
            onChange={(e) => onChange({ [key]: e.target.value })} style={missingStyle(required && blank(value[key]))} />}
    </div>
  );
  return (
    <div style={cardStyle}>
      <h3 style={headingStyle}><FaShieldAlt /> Risk Control</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px' }}>
        {field('current_controls', 'Current Controls', true, 'Controls already in place — cleaning SOP, dedicated tools, visual inspection, analytical testing…')}
        {field('proposed_controls', 'Proposed Controls / Actions', needsProposed, needsProposed ? `Required — overall risk is ${riskLevel}` : 'Additional controls to reduce the risk (optional for Low risk)')}
        <div>
          <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Evidence of Proposed Action{!blank(value.proposed_controls) && ' *'}</label>
          <EvidenceCell reference={value.control_evidence_ref} files={value.control_evidence_files || []} readOnly={readOnly} missing={evidenceMissing}
            onChange={({ reference, files }) => onChange({ control_evidence_ref: reference, control_evidence_files: files })} />
        </div>
      </div>
    </div>
  );
};

const scoreSelect = (value, onChange, missing) => (
  <select className="form-control form-control-sm" value={value || ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
    style={{ width: '54px', padding: '2px 4px', ...missingStyle(missing) }}>
    <option value="">—</option>
    {[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}
  </select>
);

const COLUMNS = ['#', 'Risk Factor', 'Failure Mode *', 'Effect', 'Cause', 'Current Controls *', 'S', 'O', 'D', 'RPN',
  'Proposed Control / Action', 'Responsible', 'Target Date', 'Action Status', 'Evidence of Action', 'Rev. S', 'Rev. O', 'Rev. D', 'Residual RPN'];

export const FmeaWorksheet = ({ items = [], onChange, readOnly, seedFrom }) => {
  const update = (idx, patch) => onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, {
    id: `fm-${Date.now()}`, risk_factor: '', failure_mode: '', effect: '', cause: '', current_controls: '',
    severity: null, occurrence: null, detectability: null, proposed_controls: '', responsible: '', target_date: '',
    action_status: 'Open', evidence_ref: '', evidence_files: [], revised_severity: null, revised_occurrence: null, revised_detectability: null,
  }]);

  const cellStyle = { padding: '6px', borderBottom: '1px solid var(--border-color)', verticalAlign: 'top', fontSize: 'var(--fs-sm)' };
  const text = (idx, key, missing, width = '160px') => (readOnly
    ? <div style={{ minWidth: width, whiteSpace: 'pre-wrap' }}>{items[idx][key] || '—'}</div>
    : <textarea className="form-control form-control-sm" rows={2} value={items[idx][key] || ''} onChange={(e) => update(idx, { [key]: e.target.value })}
        style={{ minWidth: width, fontSize: 'var(--fs-sm)', ...missingStyle(missing) }} />);
  const score = (idx, key, missing) => (readOnly ? <strong>{items[idx][key] ?? '—'}</strong> : scoreSelect(items[idx][key], (v) => update(idx, { [key]: v }), missing));

  const rpns = items.map(rowRpn).filter(Boolean);
  const residuals = items.map(rowResidualRpn).filter(Boolean);

  return (
    <div style={cardStyle}>
      <h3 style={headingStyle}>
        <FaListOl /> FMEA Worksheet
        <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 400, color: 'var(--text-secondary)' }}>
          S × O × D = RPN (1–27) · proposed control required at RPN ≥ {PROPOSED_CONTROL_MIN_RPN} · evidence required for every proposed control
        </span>
      </h3>

      {items.length === 0 ? (
        <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>
          No failure modes recorded yet.
          {!readOnly && seedFrom && (
            <div style={{ marginTop: '10px' }}>
              <button type="button" className="btn btn-sm btn-outline" onClick={() => onChange(buildDefaultFmeaItems(seedFrom))}>
                Load standard failure modes (one per risk factor)
              </button>
            </div>
          )}
        </div>
      ) : (
        <FloatingScrollWrapper>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-color)' }}>
                {COLUMNS.map((h) => (
                  <th key={h} style={{ padding: '8px 6px', textAlign: 'left', fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap', borderBottom: '2px solid var(--border-color)' }}>{h}</th>
                ))}
                {!readOnly && <th style={{ borderBottom: '2px solid var(--border-color)' }} />}
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => {
                const rpn = rowRpn(it);
                const needsProposed = rpn >= PROPOSED_CONTROL_MIN_RPN;
                return (
                  <tr key={it.id || idx}>
                    <td style={cellStyle}>{idx + 1}</td>
                    <td style={cellStyle}>{readOnly ? (it.risk_factor || '—') : (
                      <input className="form-control form-control-sm" value={it.risk_factor || ''} onChange={(e) => update(idx, { risk_factor: e.target.value })} style={{ minWidth: '110px', fontSize: 'var(--fs-sm)' }} />
                    )}</td>
                    <td style={cellStyle}>{text(idx, 'failure_mode', blank(it.failure_mode), '180px')}</td>
                    <td style={cellStyle}>{text(idx, 'effect', false)}</td>
                    <td style={cellStyle}>{text(idx, 'cause', false)}</td>
                    <td style={cellStyle}>{text(idx, 'current_controls', blank(it.current_controls), '200px')}</td>
                    <td style={cellStyle}>{score(idx, 'severity', !it.severity)}</td>
                    <td style={cellStyle}>{score(idx, 'occurrence', !it.occurrence)}</td>
                    <td style={cellStyle}>{score(idx, 'detectability', !it.detectability)}</td>
                    <td style={cellStyle}><RpnPill value={rpn} /></td>
                    <td style={cellStyle}>{text(idx, 'proposed_controls', needsProposed && blank(it.proposed_controls), '200px')}</td>
                    <td style={cellStyle}>{readOnly ? (it.responsible || '—') : (
                      <input className="form-control form-control-sm" value={it.responsible || ''} onChange={(e) => update(idx, { responsible: e.target.value })} style={{ minWidth: '110px', fontSize: 'var(--fs-sm)' }} />
                    )}</td>
                    <td style={cellStyle}>{readOnly ? (it.target_date || '—') : (
                      <input type="date" className="form-control form-control-sm" value={it.target_date || ''} onChange={(e) => update(idx, { target_date: e.target.value })} style={{ fontSize: 'var(--fs-sm)' }} />
                    )}</td>
                    <td style={cellStyle}>{readOnly ? (it.action_status || 'Open') : (
                      <select className="form-control form-control-sm" value={it.action_status || 'Open'} onChange={(e) => update(idx, { action_status: e.target.value })} style={{ minWidth: '110px', fontSize: 'var(--fs-sm)' }}>
                        {FMEA_ACTION_STATUSES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    )}</td>
                    <td style={cellStyle}>
                      <EvidenceCell compact readOnly={readOnly} reference={it.evidence_ref} files={it.evidence_files || []}
                        missing={!blank(it.proposed_controls) && !hasEvidence(it.evidence_ref, it.evidence_files)}
                        onChange={({ reference, files }) => update(idx, { evidence_ref: reference, evidence_files: files })} />
                    </td>
                    <td style={cellStyle}>{score(idx, 'revised_severity', false)}</td>
                    <td style={cellStyle}>{score(idx, 'revised_occurrence', false)}</td>
                    <td style={cellStyle}>{score(idx, 'revised_detectability', false)}</td>
                    <td style={cellStyle}><RpnPill value={rowResidualRpn(it)} /></td>
                    {!readOnly && (
                      <td style={cellStyle}>
                        <button type="button" title="Remove row" onClick={() => remove(idx)}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--danger-color)' }}><FaTrash /></button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </FloatingScrollWrapper>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '10px', flexWrap: 'wrap', fontSize: 'var(--fs-md)' }}>
        {!readOnly && (
          <button type="button" className="btn btn-sm btn-outline" onClick={add} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <FaPlus /> Add Failure Mode
          </button>
        )}
        {rpns.length > 0 && <span>Highest RPN: <RpnPill value={Math.max(...rpns)} /></span>}
        {residuals.length > 0 && <span>Highest residual RPN: <RpnPill value={Math.max(...residuals)} /></span>}
      </div>
    </div>
  );
};
